import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';

const BULK_MAX = 200;

const formatDate = (timestamp: number) =>
  new Date(timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

// Dashboard panel: request readings from a pasted list of usernames, and see
// who has not answered yet.
const MeterReadingRequestsPanel: React.FC = () => {
  const outstanding = useQuery(api.meterReadingRequests.adminListOutstanding, { limit: 50 });
  const requestForUsernames = useMutation(api.meterReadingRequests.adminRequestForUsernames);
  const [usernamesText, setUsernamesText] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const usernames = usernamesText.split(/[\s,;]+/).map((name) => name.trim()).filter(Boolean);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending || usernames.length === 0) return;
    setSending(true);
    setResult(null);
    try {
      const response = await requestForUsernames({ usernames });
      if (!response.success) {
        setResult({ ok: false, text: response.error || 'The requests were not sent.' });
        return;
      }
      const parts = [`${response.requested} requested`];
      if (response.alreadyOpen) parts.push(`${response.alreadyOpen} already had an open request`);
      if (response.notFound.length) parts.push(`not found: ${response.notFound.join(', ')}`);
      setResult({ ok: response.notFound.length === 0, text: parts.join('; ') + '.' });
      if (response.notFound.length === 0) setUsernamesText('');
    } catch (error) {
      console.error('Bulk meter reading request failed:', error);
      setResult({ ok: false, text: 'The requests were not sent. Please try again.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-12">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-medium font-sans">Meter Reading Requests</h2>
          <p className="text-sm text-gray-600 mt-1">
            Ask customers for a meter photo through the mobile app. They are reminded after 7 and 14 days.
          </p>
        </div>
        <div className="text-sm font-medium text-gray-500">{outstanding?.length ?? 0} outstanding</div>
      </div>

      <form onSubmit={handleSend} className="mb-6">
        <textarea
          value={usernamesText}
          onChange={(e) => setUsernamesText(e.target.value)}
          rows={3}
          placeholder="Paste usernames, separated by spaces, commas or new lines"
          className="w-full px-3 py-2 border border-gray-300 rounded"
        />
        <div className="mt-2 flex flex-wrap items-center gap-4">
          <button
            type="submit"
            disabled={sending || usernames.length === 0 || usernames.length > BULK_MAX}
            className="px-4 py-2 bg-[#16A34A] text-white rounded-md hover:bg-shade-green-700 disabled:opacity-50"
          >
            {sending ? 'Sending...' : `Request ${usernames.length || ''} reading${usernames.length === 1 ? '' : 's'}`}
          </button>
          {usernames.length > BULK_MAX && (
            <span className="text-sm text-red-600">Send at most {BULK_MAX} at a time.</span>
          )}
          {result && (
            <span className={`text-sm ${result.ok ? 'text-green-700' : 'text-red-600'}`}>{result.text}</span>
          )}
        </div>
      </form>

      {outstanding && outstanding.length > 0 ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-2 pr-4 font-medium">Customer</th>
              <th className="py-2 pr-4 font-medium">Requested</th>
              <th className="py-2 pr-4 font-medium">Reminders</th>
              <th className="py-2 pr-4 font-medium">Push</th>
              <th className="py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {outstanding.map((row) => (
              <tr key={row._id} className="border-b last:border-0">
                <td className="py-2 pr-4">
                  <Link to={`/admin/customer/${row.userId}`} className="text-blue-600 hover:underline">
                    {row.name || row.username}
                  </Link>
                  <span className="text-gray-500"> ({row.username})</span>
                </td>
                <td className="py-2 pr-4">{formatDate(row.requestedAt)}</td>
                <td className="py-2 pr-4">{row.remindersSent}</td>
                <td className="py-2 pr-4">{row.hasPushToken ? 'Yes' : 'Inbox only'}</td>
                <td className={`py-2 ${row.status === 'expired' ? 'text-red-600 font-medium' : ''}`}>
                  {row.status === 'expired' ? 'No response: phone them' : 'Waiting'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-sm text-gray-500">No outstanding requests.</p>
      )}
    </div>
  );
};

export default MeterReadingRequestsPanel;
